// src/components/manuscriptTheme/ThemeControlPanel.jsx
//
// The Theme Editor's left panel: the presets, then one accordion section per
// part of the book (themeSections.js). Every control writes straight into the
// theme being edited; nothing reaches the manuscript until "Apply to
// Manuscript".
//
// Chrome — the panel, its labels, its controls — comes from `src/theme.js`. The
// theme's own values (its fonts, its colours) are the author's data and are only
// ever shown, never used to style the panel.
import React, { useRef, useState } from 'react';
import {
  Box, Button, ButtonBase, IconButton, Menu, MenuItem, ListSubheader, Slider, Switch,
  ToggleButton, ToggleButtonGroup, Typography, MenuItem as Option, TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import TitleIcon from '@mui/icons-material/Title';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { tokens } from '../../theme';
import { fontStackFor } from '../../config/fonts';
import { ART_SAMPLES, PRESETS, THEME_FONT_GROUPS } from '../../config/manuscriptThemes';
import { artName, artUrl } from '../../utils/manuscriptTheme';
import ThemeMiniCard from './ThemeMiniCard';
import { SECTIONS } from './themeSections';

const ICONS = {
  palette: PaletteOutlinedIcon, page: CropPortraitIcon, text: TitleIcon, chapter: MenuBookOutlinedIcon,
  head: ViewHeadlineIcon, brk: MoreHorizIcon, quote: FormatQuoteIcon, run: VerticalAlignTopIcon,
  color: WaterDropOutlinedIcon, image: ImageOutlinedIcon,
};

const ANCHORS = ['tl', 'tc', 'tr', 'cl', 'cc', 'cr', 'bl', 'bc', 'br'];
const ANCHOR_NAMES = {
  tl: 'Top left', tc: 'Top', tr: 'Top right', cl: 'Left', cc: 'Centre', cr: 'Right', bl: 'Bottom left', bc: 'Bottom', br: 'Bottom right',
};

/** The page-art picker: the six samples, an upload tile, and what is chosen. */
function ArtPicker({ theme, art }) {
  const fileRef = useRef(null);
  const a = theme.art;
  const uploaded = a.src.startsWith('upload:');
  return (
    <Row label="Image">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.875 }}>
        {Object.entries(ART_SAMPLES).map(([id, sample]) => {
          const on = a.src === `sample:${id}`;
          const tiled = id === 'damask' || id === 'parchment';
          return (
            <ButtonBase
              key={id}
              title={sample.name}
              aria-pressed={on}
              onClick={() => art.pick(id)}
              sx={{
                aspectRatio: '2 / 3', position: 'relative', overflow: 'hidden',
                border: `1px solid ${on ? C.purple : C.line}`, boxShadow: on ? `0 0 0 2px ${C.purpleSoft}` : 'none',
                borderRadius: `${tokens.radius.panel}px`, background: C.card,
                backgroundImage: `url("${artUrl({ src: `sample:${id}` })}")`,
                backgroundSize: tiled ? 'auto 60%' : 'cover', backgroundRepeat: tiled ? 'repeat' : 'no-repeat', backgroundPosition: 'center',
              }}
            >
              <Box component="span" sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: 'rgba(255, 255, 255, 0.92)', fontSize: 9.5, fontWeight: 600, color: C.muted, py: '3px', textAlign: 'center' }}>
                {sample.name}
              </Box>
            </ButtonBase>
          );
        })}
        <ButtonBase
          onClick={() => fileRef.current?.click()}
          aria-pressed={uploaded}
          disabled={art.uploading}
          sx={{
            aspectRatio: '2 / 3', flexDirection: 'column', gap: 0.5, fontSize: 10.5, fontWeight: 600,
            color: uploaded ? 'transparent' : C.purple, background: C.purpleTint,
            border: `1px dashed ${uploaded ? C.purple : C.purpleSoft}`, borderRadius: `${tokens.radius.panel}px`,
            boxShadow: uploaded ? `0 0 0 2px ${C.purpleSoft}` : 'none',
            ...(uploaded ? { backgroundImage: `url("${artUrl(a)}")`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
          }}
        >
          <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />
          {art.uploading ? 'Uploading…' : 'Upload'}
        </ButtonBase>
      </Box>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) art.upload(f); }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, fontSize: 12, color: C.muted, mt: 1 }}>
        <span>
          {a.src
            ? <><b style={{ color: C.ink }}>{artName(a)}</b>{uploaded ? ` · ${a.w}×${a.h}px` : ' · vector'}</>
            : 'No image yet — pick a sample or upload your own (PNG, JPG or WebP, up to 15 MB)'}
        </span>
        {a.src && (
          <ButtonBase onClick={art.remove} sx={{ color: C.error, fontSize: 12, fontWeight: 600 }}>Remove</ButtonBase>
        )}
      </Box>
    </Row>
  );
}

const C = tokens.color;

const getP = (o, path) => path.split('.').reduce((a, k) => (a == null ? a : a[k]), o);

const labelSx = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.75,
};
const valueSx = { color: C.purple, fontWeight: 700, fontVariantNumeric: 'tabular-nums' };

function formatValue(value, unit) {
  const v = Number(value);
  if (unit === 'in') return `${v.toFixed(2)} in`;
  if (unit === '×') return `${v.toFixed(2)}×`;
  if (unit === 'em') return `${v.toFixed(2)} em`;
  if (unit === '%') return `${v}%`;
  if (unit === 'lines') return `${v} lines`;
  return `${v} ${unit}`;
}

function Row({ label, value, children }) {
  return (
    <Box sx={{ mt: 1.75 }}>
      <Box sx={labelSx}>
        <span>{label}</span>
        {value !== undefined && <Box component="span" sx={valueSx}>{value}</Box>}
      </Box>
      {children}
    </Box>
  );
}

const segSx = {
  width: '100%',
  background: C.purpleTintDeep,
  borderRadius: `${tokens.radius.panel}px`,
  p: '3px',
  gap: '2px',
  '& .MuiToggleButton-root': {
    flex: 1, border: 0, borderRadius: `${tokens.radius.base + 2}px !important`,
    textTransform: 'none', fontSize: 12, fontWeight: 600, color: C.muted, py: 0.75, px: 0.5,
    whiteSpace: 'nowrap',
  },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    background: C.card, color: C.plum, boxShadow: tokens.shadow.sm,
  },
};

// The ornaments and breaks, drawn the way the book draws them.
const GLYPH = {
  none: '—', rule: <span style={{ display: 'inline-block', width: '2.2em', borderTop: '1.5px solid currentColor' }} />,
  fleuron: '❦', flourish: '∿', dots: '• • •', diamond: '◆', stars: '✦',
  asterism: '* * *', dinkus: '⁂', hash: '#', space: ' ',
};

function FontButton({ value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          border: `1px solid ${C.line}`, borderRadius: `${tokens.radius.panel}px`, px: 1.5, py: 1,
          background: C.card, fontSize: 17, color: C.ink, '&:hover': { borderColor: C.purpleSoft },
        }}
      >
        {/* The typeface's own name in its own face: the author's choice, shown. */}
        <span style={{ fontFamily: fontStackFor(value) }}>{value}</span>
        <ExpandMoreIcon sx={{ color: C.muted, fontSize: 18 }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 290, maxHeight: '60vh' } } }}
      >
        {THEME_FONT_GROUPS.flatMap((g) => [
          <ListSubheader key={g.id} sx={{ fontSize: tokens.text.micro.size, letterSpacing: '.12em', textTransform: 'uppercase', color: C.muted, lineHeight: '28px' }}>
            {g.label}
          </ListSubheader>,
          ...g.fonts.map((f) => (
            <MenuItem
              key={f}
              selected={f === value}
              onClick={() => { setAnchor(null); onChange(f); }}
              sx={{ fontFamily: fontStackFor(f), fontSize: 17 }}
            >
              {f}
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
}

function Control({ row, theme, set, art }) {
  const [type] = row;
  if (type === 'dyn') {
    const [, note, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    const { tone, text } = note(theme);
    const toneSx = tone === 'warn' ? { color: C.warning, background: C.warningTint }
      : tone === 'good' ? { color: C.success, background: C.successTint } : { color: C.muted };
    return (
      <Typography sx={{ fontSize: 12, lineHeight: 1.5, mt: 1.5, borderRadius: `${tokens.radius.panel}px`, ...(tone === 'plain' ? {} : { px: 1.4, py: 1.1 }), ...toneSx }}>
        {text}
      </Typography>
    );
  }
  if (type === 'artpick') return <ArtPicker theme={theme} art={art} />;
  if (type === 'head') {
    const [, text, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Typography sx={{
        fontSize: tokens.text.micro.size, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase',
        color: C.muted, mt: 2.75, pt: 1.75, borderTop: `1px dashed ${C.line}`,
      }}
      >
        {text}
      </Typography>
    );
  }
  if (type === 'note') {
    const [, text, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return <Typography sx={{ fontSize: 12, color: C.muted, lineHeight: 1.5, mt: 1.5 }}>{text}</Typography>;
  }

  const [, path, label] = row;
  const value = getP(theme, path);

  if (type === 'range') {
    const [, , , min, max, step, unit, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Row label={label} value={formatValue(value, unit)}>
        <Slider
          size="small"
          min={min} max={max} step={step}
          value={Number(value)}
          onChange={(_, v) => set(path, v)}
          aria-label={label}
          sx={{ color: C.purple, py: 1 }}
        />
      </Row>
    );
  }
  if (type === 'select') {
    const [, , , options, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Row label={label}>
        <TextField
          select fullWidth size="small" value={value}
          onChange={(e) => set(path, e.target.value)}
          inputProps={{ 'aria-label': label }}
          sx={{ '& .MuiInputBase-root': { fontSize: 13.5, background: C.card } }}
        >
          {options.map(([k, n]) => <Option key={k} value={k} sx={{ fontSize: 13.5 }}>{n}</Option>)}
        </TextField>
      </Row>
    );
  }
  if (type === 'seg') {
    const [, , , options, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Row label={label}>
        <ToggleButtonGroup
          exclusive size="small" value={value} aria-label={label} sx={segSx}
          onChange={(_, v) => { if (v !== null) set(path, v); }}
        >
          {options.map(([k, n]) => <ToggleButton key={k} value={k}>{n}</ToggleButton>)}
        </ToggleButtonGroup>
      </Row>
    );
  }
  if (type === 'toggle') {
    const [, , , showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Box
        component="label"
        sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, fontSize: 13, color: C.ink, cursor: 'pointer' }}
      >
        <span>{label}</span>
        <Switch
          size="small" checked={Boolean(value)} onChange={(e) => set(path, e.target.checked)}
          sx={{ '& .Mui-checked': { color: `${C.purple} !important` }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: `${C.purple} !important` } }}
        />
      </Box>
    );
  }
  if (type === 'font') {
    const [, , , showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return <Row label={label}><FontButton value={value} onChange={(f) => set(path, f)} /></Row>;
  }
  if (type === 'glyph') {
    const [, , , options, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Row label={label}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.75 }}>
          {options.map(([k, n]) => {
            const on = value === k;
            return (
              <ButtonBase
                key={k}
                onClick={() => set(path, k)}
                title={n}
                aria-pressed={on}
                sx={{
                  height: 52, flexDirection: 'column', gap: '3px',
                  border: `1px solid ${on ? C.purple : C.line}`,
                  boxShadow: on ? `0 0 0 2px ${C.purpleSoft}` : 'none',
                  background: on ? C.purpleTint : C.card,
                  borderRadius: `${tokens.radius.panel}px`,
                  fontSize: 17, color: C.ink,
                  fontFamily: fontStackFor(theme.body.font),
                }}
              >
                <span>{GLYPH[k]}</span>
                <Box component="small" sx={{ fontFamily: tokens.family.sans, fontSize: 9.5, fontWeight: 600, color: C.muted }}>{n}</Box>
              </ButtonBase>
            );
          })}
        </Box>
      </Row>
    );
  }
  if (type === 'chips') {
    const [, , , options, showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Row label={label}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {options.map(([k, n]) => {
            const on = Boolean(value?.[k]);
            return (
              <ButtonBase
                key={k}
                aria-pressed={on}
                onClick={() => set(path, { ...value, [k]: !on })}
                sx={{
                  borderRadius: 999, px: 1.4, py: 0.75, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${on ? C.purple : C.line}`,
                  background: on ? C.purple : C.card, color: on ? C.card : C.muted,
                }}
              >
                {n}
              </ButtonBase>
            );
          })}
        </Box>
      </Row>
    );
  }
  if (type === 'anchor') {
    const [, , , showIf] = row;
    if (showIf && !showIf(theme)) return null;
    return (
      <Row label={label} value={ANCHOR_NAMES[value]}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 26px)', gap: 0.5, background: C.purpleTintDeep, p: 0.75, borderRadius: `${tokens.radius.panel}px`, width: 'max-content' }}>
          {ANCHORS.map((k) => {
            const on = value === k;
            return (
              <ButtonBase
                key={k}
                title={ANCHOR_NAMES[k]}
                aria-label={ANCHOR_NAMES[k]}
                aria-pressed={on}
                onClick={() => set(path, k)}
                sx={{ width: 26, height: 26, borderRadius: `${tokens.radius.base + 2}px`, background: on ? C.purple : C.card }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: on ? C.card : C.purpleSoft }} />
              </ButtonBase>
            );
          })}
        </Box>
      </Row>
    );
  }
  if (type === 'color') {
    const [, , , swatches] = row;
    return (
      <Row label={label} value={value}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {swatches.map((hex) => {
            const on = hex.toLowerCase() === String(value).toLowerCase();
            return (
              <ButtonBase
                key={hex}
                title={hex}
                aria-label={`${label} ${hex}`}
                aria-pressed={on}
                onClick={() => set(path, hex)}
                sx={{
                  width: 30, height: 30, borderRadius: `${tokens.radius.panel}px`,
                  // The swatch is the author's colour, shown as itself.
                  background: hex,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: on ? `0 0 0 2px ${C.card}, 0 0 0 4px ${C.purple}` : 'none',
                }}
              />
            );
          })}
          <Box
            component="input"
            type="color"
            title="Custom color"
            aria-label={`Custom ${label.toLowerCase()} colour`}
            value={value}
            onChange={(e) => set(path, e.target.value)}
            sx={{ width: 34, height: 32, border: 0, background: 'none', p: 0, cursor: 'pointer' }}
          />
        </Box>
      </Row>
    );
  }
  return null;
}

function PresetCard({ theme, selected, onPick, onDelete, blurb, labelStyle }) {
  // The delete button sits beside the card, not inside it: a button inside a
  // button cannot be reached properly by keyboard or screen reader. It shows on
  // hover, and whenever it has focus.
  return (
    <Box sx={{ position: 'relative', '&:hover .rv-preset-del, & .rv-preset-del:focus-visible': { opacity: 1 } }}>
      <ButtonBase
        onClick={onPick}
        aria-pressed={selected}
        sx={{
          display: 'block', width: '100%', textAlign: 'left', p: '7px',
          border: `1px solid ${selected ? C.purple : C.line}`,
          boxShadow: selected ? `0 0 0 2px ${C.purpleSoft}` : 'none',
          borderRadius: `${tokens.radius.card}px`, background: C.card,
        }}
      >
        <ThemeMiniCard theme={theme} labelStyle={labelStyle} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.ink, m: '8px 2px 1px' }}>{theme.name}</Typography>
        <Typography sx={{ fontSize: 10.5, color: C.muted, m: '0 2px 2px', lineHeight: 1.35 }}>{blurb}</Typography>
      </ButtonBase>
      {onDelete && (
        <IconButton
          className="rv-preset-del"
          size="small"
          aria-label={`Delete ${theme.name}`}
          onClick={onDelete}
          sx={{
            opacity: 0, position: 'absolute', top: 4, right: 4, width: 22, height: 22,
            transition: `opacity ${tokens.motion.fast}ms`,
            background: C.plum, color: C.card, '&:hover': { background: C.plumInk },
            '@media (hover: none)': { opacity: 1 },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
}

function Presets({ work, saved, onPick, onDelete, onImport }) {
  return (
    <>
      <Typography sx={{ fontSize: 12, color: C.muted, lineHeight: 1.5, mt: 0.5 }}>
        Start from a preset, then fine-tune any element below. Everything you change applies
        to <b>every chapter</b>.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
        {PRESETS.map((p) => (
          <PresetCard
            key={p.id} theme={p} blurb={p.blurb} labelStyle={p.labelStyle}
            selected={work.sourceId === p.id}
            onPick={() => onPick(p, p.id)}
          />
        ))}
      </Box>
      {saved.length > 0 && (
        <>
          <Typography sx={{ fontSize: tokens.text.micro.size, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase', color: C.muted, mt: 2.5 }}>
            Your themes
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
            {saved.map((t) => (
              <PresetCard
                key={t.id} theme={t} blurb="Your custom theme"
                selected={work.sourceId === t.id}
                onPick={() => onPick(t, t.id)}
                onDelete={() => onDelete(t)}
              />
            ))}
          </Box>
        </>
      )}
      <Button
        fullWidth variant="outlined" onClick={onImport}
        sx={{ mt: 1.75, textTransform: 'none', color: C.plum, borderColor: C.line, fontWeight: 600 }}
      >
        Import theme…
      </Button>
    </>
  );
}

export default function ThemeControlPanel({
  work, saved, openId, onToggle, set, onPickPreset, onDeleteSaved, onImport,
  // The page art picker's actions: { pick(sampleId), upload(file), remove(), uploading }
  art,
  // The manuscript's trim size in inches, for the bleed note: { w, h }
  trimInches,
}) {
  const noted = { ...work, trimInches };
  return (
    <Box component="nav" aria-label="Theme settings">
      {SECTIONS.map((sec) => {
        const Icon = ICONS[sec.icon];
        const open = openId === sec.id;
        return (
          <Box key={sec.id} sx={{ borderBottom: `1px solid ${C.purpleTintDeep}` }}>
            <ButtonBase
              onClick={() => onToggle(sec.id)}
              aria-expanded={open}
              sx={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.75,
                textAlign: 'left', background: open ? C.purpleTint : C.card, '&:hover': { background: C.purpleTint },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: `${tokens.radius.panel}px`, background: C.purpleTintDeep,
                color: C.purple, display: 'grid', placeItems: 'center', flexShrink: 0,
              }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{sec.title}</Typography>
                <Typography noWrap sx={{ fontSize: 12, color: C.muted, mt: '2px' }}>{sec.sum(work)}</Typography>
              </Box>
              <ExpandMoreIcon sx={{ color: C.muted, transition: `transform ${tokens.motion.base}ms`, transform: open ? 'rotate(180deg)' : 'none' }} />
            </ButtonBase>
            {open && (
              <Box sx={{ px: 2.25, pt: 0.25, pb: 2.75, background: C.purpleTint }}>
                {sec.id === 'presets' ? (
                  <Presets work={work} saved={saved} onPick={onPickPreset} onDelete={onDeleteSaved} onImport={onImport} />
                ) : (
                  sec.rows.map((row, i) => (
                    <Control key={`${row[0]}:${typeof row[1] === 'string' ? row[1] : i}`} row={row} theme={noted} set={set} art={art} />
                  ))
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
